// src/contracts/schema/http.ts
import { z } from "zod/v4";
var coerceToContractAbsoluteBase = (base) => {
  const validatedUrl = new URL(base);
  const href = validatedUrl.href;
  if (href.endsWith("/")) {
    return href;
  } else {
    return `${href}/`;
  }
};
var normalizeSchemaValue = (value) => {
  if (value !== void 0) {
    return value;
  }
  return z.never();
};
var createContractRequest = (parts) => {
  return {
    pathParams: normalizeSchemaValue(parts?.pathParams),
    queryParams: normalizeSchemaValue(parts?.queryParams),
    body: normalizeSchemaValue(parts?.body)
  };
};
var createContractResponse = (parts) => {
  return { body: normalizeSchemaValue(parts?.body) };
};
var createContract = (args) => {
  const request = createContractRequest({
    pathParams: args.request?.pathParams,
    queryParams: args.request?.queryParams,
    body: args.request?.body
  });
  const response = createContractResponse({
    body: args.response?.body
  });
  return {
    method: args.method,
    path: args.path,
    request,
    response
  };
};
var getContractUrl = (params) => {
  const { path } = params.contract;
  const onlyPath = path.split("?")[0];
  const onlyQueryTemplate = path.split("?")[1];
  const getReplacedPath = () => {
    if (params.pathParams && typeof params.pathParams === "object") {
      return Object.entries(params.pathParams).reduce((acc, [key, value]) => {
        const regex = new RegExp(`:${key}(?=/|$)`, "g");
        return acc.replace(regex, encodeURIComponent(String(value)));
      }, onlyPath);
    }
    return onlyPath;
  };
  const getQueryParts = () => {
    const hasTemplate = typeof onlyQueryTemplate === "string";
    if (params.queryParams && typeof params.queryParams === "object") {
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(params.queryParams)) {
        if (value === void 0) continue;
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, String(v)));
        } else {
          searchParams.set(key, String(value));
        }
      }
      const queryString2 = searchParams.toString();
      return { queryString: queryString2, includeQuestion: queryString2.length > 0 || hasTemplate };
    }
    if (hasTemplate) {
      return { queryString: `${onlyQueryTemplate}`, includeQuestion: true };
    }
    return { queryString: "", includeQuestion: false };
  };
  const { queryString, includeQuestion } = getQueryParts();
  if (typeof URL === "function") {
    const url = new URL(`.${getReplacedPath()}${includeQuestion ? `?${queryString}` : ""}`, params.base);
    return url.toString();
  } else {
    const baseWithoutTrailingSlash = params.base.endsWith("/") ? params.base.slice(0, -1) : params.base;
    return `${baseWithoutTrailingSlash}${getReplacedPath()}${includeQuestion ? `?${queryString}` : ""}`;
  }
};
var withContractBase = (baseParams) => {
  return {
    withFetchParams: (urlParams) => {
      const url = getContractUrl({
        contract: baseParams.contract,
        base: baseParams.base,
        pathParams: urlParams.pathParams,
        queryParams: urlParams.queryParams
      });
      const { method, response } = baseParams.contract;
      const defaultFetcher = async (args) => {
        const response2 = await fetch(args.url, {
          method: args.method,
          body: args.body ? JSON.stringify(args.body) : void 0
        });
        return response2.json();
      };
      const dataHandlers = (data) => {
        return {
          toRaw: () => data,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          toParsed: async () => response.body.parse(await data)
        };
      };
      return {
        withFetcher: ((fetcher) => {
          const effectiveFetcher = fetcher ?? defaultFetcher;
          return dataHandlers(effectiveFetcher({ method, url, body: urlParams.body }));
        })
      };
    }
  };
};

// src/contracts/data-dump-http.ts
import * as z2 from "zod/v4";
var DataDumpSourceSchema = z2.union([
  z2.literal("machine-learning-face-detection"),
  z2.literal("machine-learning-object-detection")
]);
var DataDumpMimeTypeSchema = z2.union([
  z2.literal("application/json"),
  z2.literal("application/octet-stream"),
  z2.literal("application/xml")
]);
var DataDumpGrantContract = createContract({
  method: "POST",
  path: "/data-dump/:proctoringSessionUuid/grant",
  request: {
    pathParams: z2.object({
      proctoringSessionUuid: z2.string()
    }),
    body: z2.object({
      source: DataDumpSourceSchema,
      mimeType: DataDumpMimeTypeSchema
    })
  },
  response: {
    body: z2.object({
      grantId: z2.string(),
      uploadUrl: z2.string()
    })
  }
});
var DataDumpCallbackContract = createContract({
  method: "POST",
  path: "/data-dump/:proctoringSessionUuid/callback",
  request: {
    pathParams: z2.object({
      proctoringSessionUuid: z2.string()
    }),
    body: z2.object({
      grantId: z2.string()
    })
  }
});
var DataDumpLinksContract = createContract({
  method: "GET",
  path: "/data-dump/:proctoringSessionUuid/links",
  request: {
    pathParams: z2.object({
      proctoringSessionUuid: z2.string()
    })
  },
  response: {
    body: z2.object({
      links: z2.array(
        z2.object({
          source: DataDumpSourceSchema,
          mimeType: DataDumpMimeTypeSchema,
          downloadUrl: z2.string()
        })
      )
    })
  }
});

// src/contracts/logging-http.ts
import * as z3 from "zod/v4";
var MachineLearningEventSchema = z3.object({
  type: z3.literal("machine-learning"),
  time: z3.date()
});
var FaceDetectionStateSchema = z3.union([
  z3.literal("detected-no-face"),
  z3.literal("detected-target-face"),
  z3.literal("detected-unknown-face"),
  z3.literal("detected-many-faces")
]);
var FaceDetectionEventSchema = MachineLearningEventSchema.extend({
  name: z3.literal("face-detection"),
  details: z3.object({
    previousState: FaceDetectionStateSchema,
    currentState: FaceDetectionStateSchema
  })
});
var TargetObjectsSchema = z3.union([z3.literal("cell-phone"), z3.literal("laptop"), z3.literal("book")]);
var ObjectDetectionStateSchema = z3.union([
  z3.literal("detected-no-object"),
  z3.templateLiteral(["detected-", TargetObjectsSchema], "-object"),
  z3.literal("detected-many-objects")
]);
var ObjectDetectionEventSchema = MachineLearningEventSchema.extend({
  name: z3.literal("object-detection"),
  details: z3.object({
    previousState: ObjectDetectionStateSchema,
    currentState: ObjectDetectionStateSchema
  })
});
var LogMachineLearningQuizEventContract = createContract({
  method: "POST",
  path: "/quiz/event/:proctoringSessionUuid",
  request: {
    pathParams: z3.object({
      proctoringSessionUuid: z3.string()
    }),
    body: z3.union([FaceDetectionEventSchema, ObjectDetectionEventSchema])
  }
});

// src/contracts/restricted-device-event.ts
import * as z5 from "zod/v4";

// src/contracts/schema/event.ts
import * as z4 from "zod/v4";
var createEventContract = (namespace, handshake, events) => {
  return {
    namespace,
    queryParams: handshake.queryParams,
    events
  };
};
var createDiscriminableEventPayload = (key, schema) => {
  return z4.object({
    key: z4.literal(key),
    ...schema.shape
  });
};
var createDiscriminableEvent = (...args) => {
  return z4.discriminatedUnion("key", args);
};

// src/contracts/restricted-device-event.ts
var RestrictedDeviceStateSchema = z5.object({
  isLocked: z5.boolean()
});
var RestrictedDeviceEventContract = createEventContract(
  "/restricted-device",
  {
    queryParams: z5.object({
      secret: z5.string(),
      peerId: z5.string()
    })
  },
  {
    message: createDiscriminableEvent(
      createDiscriminableEventPayload("pollRequest", z5.object({})),
      createDiscriminableEventPayload("pollResponse", z5.object({
        devices: z5.array(z5.object({
          peerId: z5.string(),
          connected: z5.boolean(),
          data: z5.any()
        }))
      })),
      createDiscriminableEventPayload("stateUpdate", RestrictedDeviceStateSchema)
    )
  }
);

// src/contracts/restricted-device-http.ts
import * as z7 from "zod/v4";

// src/contracts/restricted-device-common.ts
import * as z6 from "zod/v4";
var RestrictedDeviceRoleSchema = z6.union(
  [
    z6.literal("source"),
    z6.literal("sink")
  ]
);
var RestrictedDevicePlatformSchema = z6.union(
  [
    z6.literal("extension"),
    z6.literal("mobile"),
    z6.literal("lockdown")
  ]
);

// src/contracts/restricted-device-http.ts
var RestrictedDeviceRegistrationContract = createContract({
  method: "POST",
  path: "/restricted-device/register",
  request: {
    body: z7.object({
      role: RestrictedDeviceRoleSchema,
      platform: RestrictedDevicePlatformSchema,
      proctoringSessionUuid: z7.string()
    })
  },
  response: {
    body: z7.object({
      peerId: z7.string()
    })
  }
});
var RestrictedDeviceGrantContract = createContract({
  method: "POST",
  path: "/restricted-device/:restrictedDevicePeerId/grant",
  request: {
    pathParams: z7.object({
      restrictedDevicePeerId: z7.string()
    })
  },
  response: {
    body: z7.object({
      secret: z7.string()
    })
  }
});
export {
  DataDumpCallbackContract,
  DataDumpGrantContract,
  DataDumpLinksContract,
  DataDumpMimeTypeSchema,
  DataDumpSourceSchema,
  FaceDetectionEventSchema,
  FaceDetectionStateSchema,
  LogMachineLearningQuizEventContract,
  MachineLearningEventSchema,
  ObjectDetectionEventSchema,
  ObjectDetectionStateSchema,
  RestrictedDeviceEventContract,
  RestrictedDeviceGrantContract,
  RestrictedDeviceRegistrationContract,
  RestrictedDeviceStateSchema,
  TargetObjectsSchema,
  coerceToContractAbsoluteBase,
  getContractUrl,
  withContractBase
};
//# sourceMappingURL=index.js.map